import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  type ChatSessionResponse,
  chatGetSessionOptions,
  chatGetSessionQueryKey,
  chatUpdateSessionMutation,
  type ReasoningEffort,
} from "@/api";
import { HISTORY_ATTACHMENTS_PAGE } from "./MessageAttachments";

/**
 * The chat session request: each message carries a first page of its attachments plus their count (MHS-646),
 * so a chat with thousands of files loads a bounded response. Use it for every exact cache key of a session.
 */
export const chatSessionRequest = (sessionUuid: string) => ({
  path: { session_uuid: sessionUuid },
  query: { attachmentsLimit: HISTORY_ATTACHMENTS_PAGE },
});

/** An open chat (session + saved messages). Disabled for a new chat. */
export function useChatSession(sessionUuid: string | undefined) {
  return useQuery({
    ...chatGetSessionOptions(chatSessionRequest(sessionUuid ?? "")),
    enabled: Boolean(sessionUuid),
    select: (r) => r.data,
  });
}

/**
 * The chat's thinking level. A new chat holds it locally until the first
 * send; an open chat saves each change to the session (optimistically), and
 * its stored value is never cleared just because models are still loading.
 */
export function useChatEffort(sessionUuid: string | undefined, session: ChatSessionResponse | undefined) {
  const queryClient = useQueryClient();
  const [draftEffort, setDraftEffort] = useState<ReasoningEffort | null>(null);
  const key = chatGetSessionQueryKey(chatSessionRequest(sessionUuid ?? ""));

  const save = useMutation({
    ...chatUpdateSessionMutation(),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ data: ChatSessionResponse }>(key);
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          data: { ...previous.data, reasoningEffort: vars.body.reasoningEffort ?? null },
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  if (!sessionUuid) return { effort: draftEffort, setEffort: setDraftEffort };
  return {
    effort: (session?.reasoningEffort ?? null) as ReasoningEffort | null,
    setEffort: (effort: ReasoningEffort | null) =>
      save.mutate({ path: { session_uuid: sessionUuid }, body: { reasoningEffort: effort } }),
  };
}
